import { ReactNode } from 'react';

import Stack from '@mui/material/Stack';

import { SxProps } from '@mui/material/styles';

import { EmptyBox } from './emptyBox';
import { ErrorBox, Props as ErrorBoxProps } from './errorBox';
import { Loader } from './loader';

interface Props<Data> {
  // When supplied, this overrides the loading spinner
  LoadingSkeleton?: ({ children }: { children?: ReactNode }) => ReactNode;
  children: (data: Exclude<Data, undefined | null>) => ReactNode;
  data: Data;
  emptyAction?: ReactNode;
  emptyCondensed?: boolean;
  emptyImage?: ReactNode;
  emptyMessage?: ReactNode;
  emptySx?: SxProps;
  emptyTitle?: ReactNode;
  errorBoxProps?: ErrorBoxProps;
  isEmpty?: boolean;
  isError: boolean;
  isLoading?: boolean;
  loaderText?: string;
}

export function ApiDataWrapper<Data>({
  LoadingSkeleton,
  children,
  data,
  emptyAction,
  emptyCondensed,
  emptyImage,
  emptyMessage,
  emptySx,
  emptyTitle,
  errorBoxProps = {},
  isEmpty,
  isError,
  isLoading,
  loaderText,
}: Props<Data>) {
  if (isError) {
    return <ErrorBox {...errorBoxProps} />;
  }

  if (isLoading || (!isError && !data && isLoading === false)) {
    return (
      <>
        {LoadingSkeleton ? (
          <LoadingSkeleton />
        ) : (
          <Stack padding={1} width="100%">
            <Loader text={loaderText} />
          </Stack>
        )}
      </>
    );
  }

  if (isEmpty) {
    return (
      <EmptyBox
        action={emptyAction}
        condensed={emptyCondensed}
        image={emptyImage}
        message={emptyMessage}
        sx={emptySx}
        title={emptyTitle || 'No data'}
      />
    );
  }

  return <>{children(data as Exclude<Data, undefined | null>)}</>;
}
